import { Box, Container, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import ActiveCampaignEmbed from '../components/ActiveCampaignEmbed';
import SectionHeader from '../components/atomic/SectionHeader';
import Footer from '../components/Footer';
import FormErrorBoundary from '../components/FormErrorBoundary';
import Header from '../components/Header';

/**
 * Reusable layout component for ActiveCampaign form pages with consistent structure
 *
 * This component provides a standardized layout for all ActiveCampaign form-based pages.
 * It eliminates code duplication and ensures consistent styling across:
 * - Contact forms (Discuss Project)
 * - Assessment forms (GenAI Assessment)
 * - Business outcomes (HCLS Modernization)
 * - Early access programs (EllaFin)
 *
 * FEATURES:
 * - Standard header and footer
 * - SectionHeader with green bars (dark theme) for page title/subtitle
 * - White card container with rounded corners and shadow
 * - ActiveCampaign form embedding with loading state
 * - Error boundary for form failures
 * - Optional iframe fallback for offline/error scenarios
 *
 * WHEN TO USE:
 * - Use this layout when embedding ActiveCampaign forms (.txt files)
 * - Use when you want the standard dark header with green bars
 * - Use when your page follows the "header → form card" pattern
 *
 * WHEN NOT TO USE:
 * - For custom form implementations (like Sign In page with custom fields)
 * - For pages with unique layouts that don't fit the standard pattern
 * - For pages that need additional content between header and form
 * - For pages using SectionHeader without forms (use SectionHeader directly)
 *
 * EXAMPLE USAGE:
 * ```jsx
 * <FormPageLayout
 *   title="Let's Discuss Your Project"
 *   subtitle="Partner with The Collective"
 *   formHeading="Contact Form"
 *   formFilePath="/forms/discuss-project-form.txt"
 *   errorMessage="Unable to load contact form"
 *   loadingMessage="Loading form..."
 *   fallbackIframe="https://example.com/form/123"
 * />
 * ```
 *
 * @param {Object} props - Component props
 * @param {string} props.title - Page title displayed in SectionHeader (with green bars)
 * @param {string} props.subtitle - Page subtitle displayed in SectionHeader
 * @param {string} [props.formHeading] - Heading displayed inside the white form card. Omit when the ActiveCampaign form already carries its own title
 * @param {string} props.formFilePath - Path to the ActiveCampaign form HTML file (e.g., '/form.txt')
 * @param {Record<string, string>} [props.prefill] - Field values to pre-populate in the form, keyed by input `name`
 * @param {string} [props.errorMessage] - Custom error message for form loading failures
 * @param {string} [props.fallbackIframe] - Optional iframe URL to use as fallback if form fails to load
 * @param {string} [props.loadingMessage] - Custom loading message while form is fetching
 * @returns {JSX.Element} The form page layout
 */
function FormPageLayout({
  title,
  subtitle,
  formHeading,
  formFilePath,
  prefill,
  errorMessage = 'Unable to load form',
  fallbackIframe,
  loadingMessage = 'Loading form...',
}) {
  const [formHTML, setFormHTML] = useState('');

  useEffect(() => {
    // Fetch the form HTML from the text file in the public folder
    fetch(formFilePath)
      .then((response) => response.text())
      .then((html) => setFormHTML(html))
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error('Error loading form:', error);
      });
  }, [formFilePath]);

  return (
    <>
      <Header
        scrollToCollective={undefined}
        scrollToServices={undefined}
        scrollToContact={undefined}
      />

      <Box
        sx={{
          width: '100vw',
          backgroundColor: 'var(--color-background-light)',
          minHeight: '100vh',
          pt: { xs: 8, md: 10 },
          pb: 8,
        }}
      >
        {/* Header with green bars - using SectionHeader atomic component with dark theme */}
        <SectionHeader title={title} subtitle={subtitle} dark={true} />

        {/* ActiveCampaign Form Container */}
        <Container maxWidth="md">
          <Box
            sx={{
              backgroundColor: 'var(--color-card)',
              borderRadius: '12px',
              p: { xs: 3, md: 5 },
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              border: '1px solid var(--color-border)',
            }}
          >
            {formHeading && (
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 600,
                  mb: 4,
                  color: 'var(--color-heading)',
                  textAlign: 'center',
                }}
              >
                {formHeading}
              </Typography>
            )}

            {/* ActiveCampaign Form Embed */}
            <FormErrorBoundary fallbackMessage={errorMessage}>
              {formHTML && (
                <ActiveCampaignEmbed
                  htmlContent={formHTML}
                  prefill={prefill}
                  hideTitle={Boolean(formHeading)}
                />
              )}

              {/* Loading state with optional iframe fallback */}
              {!formHTML && (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography
                    variant="body2"
                    sx={{ color: 'var(--color-text-muted)', mb: 3 }}
                  >
                    {loadingMessage}
                  </Typography>
                  {/* Optional fallback to iframe if provided */}
                  {fallbackIframe && (
                    <iframe
                      src={fallbackIframe}
                      width="100%"
                      height="700"
                      style={{ border: 'none' }}
                      title={formHeading}
                    />
                  )}
                </Box>
              )}
            </FormErrorBoundary>
          </Box>
        </Container>
      </Box>

      <Footer />
    </>
  );
}

export default FormPageLayout;
